"use client";

import type * as React from "react";

import type { ChartConfig } from "./chart";

/**
 * Shared building blocks for the series-based chart families (area, bar, line,
 * radar).
 *
 * Recharts detects axis/grid/series by inspecting the *element type* of each
 * direct child of the chart, so these helpers are exported as plain prop objects
 * (spread onto the real Recharts elements) rather than as wrapper components —
 * wrapping `XAxis`/`CartesianGrid` in a custom component would hide them from
 * Recharts' child-type detection and silently drop the axis or grid.
 */

/** Default `CartesianGrid` props: horizontal rules only. */
export const CHART_GRID_PROPS = { vertical: false } as const;

/** Default axis chrome shared across cartesian charts. */
export const CHART_AXIS_PROPS = {
	tickLine: false,
	axisLine: false,
	tickMargin: 8,
} as const;

/**
 * Base props for the families that plot one or more named series against a
 * category. Colours are never passed here — each series colour is resolved at
 * render time from the {@link ChartConfig} as `var(--color-<key>)`, so the
 * palette always lives at the call site.
 */
export type SeriesChartProps = {
	/** Row data. Each datum holds the category value plus one field per series. */
	data: Array<Record<string, unknown>>;
	/** Series styling/labels keyed by series id. Supplies the palette. */
	config: ChartConfig;
	/** Keys in `config`/`data` to draw as series, in draw order. */
	series: string[];
	/** Key on each datum used for the category axis. */
	categoryKey: string;
	/** Extra classes forwarded to the underlying `ChartContainer`. */
	className?: string;
	/** Formats series values shown in the tooltip. */
	valueFormatter?: (value: number | string) => React.ReactNode;
	/** Hides the default hover tooltip (shown by default). */
	hideTooltip?: boolean;
	/** Shows a legend. Defaults to `true` when more than one series is drawn. */
	showLegend?: boolean;
};

/** {@link SeriesChartProps} plus the axis chrome only the cartesian families have. */
export type CartesianChartProps = SeriesChartProps & {
	/** Formats the category axis tick labels. */
	categoryFormatter?: (value: string) => string;
};
